import { createSlice } from '@reduxjs/toolkit';
import { segKeyOf, compareClasses, toFareNum, availEntryOf, resolveDisplayClass } from '../../utils/seatAvailability';

const initialState = {
    allRoutes: [],
    trainRoutes: [],
    flightRoutes: [],
    busRoutes: [],
    mixedRoutes: [],
    loading: false,
    headerLoading: false,
    error: null,
    // Bumped by every availability batch that has automatic results. Manual
    // refreshes don't bump it, so the list order is not recomputed for them.
    availAutoVersion: 0,
};

const travelSlice = createSlice({
    name: 'travel',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setHeaderLoading: (state, action) => {
            state.headerLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        clearRoutes: (state) => {
            state.allRoutes = [];
            state.trainRoutes = [];
            state.flightRoutes = [];
            state.busRoutes = [];
            state.mixedRoutes = [];
            state.error = null;
        },

        addRoutes: (state, action) => {
            const newRoutes = action.payload || [];
            if (!Array.isArray(newRoutes)) return;

            state.allRoutes.push(...newRoutes);

            newRoutes.forEach(route => {
                const nodes = route.nodes || [];
                const routeType = route.route_type;

                // Determine modes present in this route
                let hasFlight = false;
                let hasTrain = false;
                let hasBus = false;
                let isMixed = false;

                // 1. Prioritize route_type from backend if available (1=Train, 2=Flight, 3=Bus, 4=Mixed)
                if (routeType === 1) {
                    hasTrain = true;
                } else if (routeType === 2) {
                    hasFlight = true;
                } else if (routeType === 3) {
                    hasBus = true;
                } else if (routeType === 4) {
                    isMixed = true;
                } else {
                    // 2. Fallback to property-based detection if route_type is missing/unknown
                    nodes.forEach(n => {
                        const seg = n.segment || {};
                        const isFlight = !!(seg.flight_num || (seg.carrier && !seg.train_name));
                        const isTrain = !!(seg.train_name || seg.train_num);
                        const isBus = !!(seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num));

                        if (isFlight) hasFlight = true;
                        if (isTrain) hasTrain = true;
                        if (isBus) hasBus = true;
                    });
                }

                const modesCount = [hasTrain, hasFlight, hasBus].filter(Boolean).length;

                if (isMixed || modesCount > 1) {
                    state.mixedRoutes.push(route);
                } else if (hasTrain) {
                    state.trainRoutes.push(route);
                } else if (hasFlight) {
                    state.flightRoutes.push(route);
                } else if (hasBus) {
                    state.busRoutes.push(route);
                }
            });
        },

        // Patches live seat availability into every matching train segment, in every
        // route list. Payload: { pref, updates: [{ segKey, cls, info, manual }] } where
        // info is the seat_avail_ui object from the get_avail_only GET.
        // - every class: stored in full under segment.avail_by_class[cls]
        // - the shown class: the class the user last refreshed by hand, otherwise
        //   preferred class if available, else the nearest available class, else the
        //   preferred class.
        // - segment.seat_avail_ui is REPLACED with the shown class's full response
        //   (explanation, comment, is_alt_avail, src/dest...), so no stale note survives.
        //   seat_class_ui and seat_costs follow it, and route.total_fare moves by the
        //   fare difference, so sorting and prices always use the shown class.
        applySegmentAvailability: (state, action) => {
            const payload = action.payload || {};
            const updates = Array.isArray(payload) ? payload : (payload.updates || []);
            const pref = Array.isArray(payload) ? null : (payload.pref || null);
            if (updates.length === 0) return;

            if (updates.some(u => u && !u.manual)) state.availAutoVersion += 1;

            const bySeg = new Map();
            updates.forEach(u => {
                if (!u?.segKey || !u.cls || !u.info) return;
                if (!bySeg.has(u.segKey)) bySeg.set(u.segKey, []);
                bySeg.get(u.segKey).push(u);
            });
            if (bySeg.size === 0) return;

            const patchRoutes = (routes) => {
                routes.forEach(route => {
                    const nodes = route.nodes;
                    if (!nodes) return;
                    nodes.forEach(node => {
                        const seg = node.segment;
                        if (!seg || !seg.train_num) return;
                        const list = bySeg.get(segKeyOf(seg));
                        if (!list) return;

                        const nextAvail = { ...(seg.avail_by_class || {}) };
                        let pinned = seg.user_pinned_class || null;
                        list.forEach(({ cls, info, manual }) => {
                            if (manual) pinned = cls;
                            nextAvail[cls] = {
                                ...info,
                                avl: info.avl ?? null,
                                fare: info.fare ?? null,
                                is_avail: info.is_avail ?? null,
                                quota: info.quota ?? null,
                            };
                        });
                        seg.avail_by_class = nextAvail;
                        if (pinned && pinned !== seg.user_pinned_class) seg.user_pinned_class = pinned;

                        const display = (pinned && availEntryOf(nextAvail, pinned))
                            ? pinned
                            : resolveDisplayClass(seg, pref, nextAvail);
                        if (!display) return;
                        const entry = availEntryOf(nextAvail, display);
                        const prevCls = seg.seat_class_ui;
                        const changed = !compareClasses(display, prevCls);
                        if (!changed && !entry) return;

                        const oldFare = toFareNum(seg.seat_avail_ui?.fare ?? (prevCls ? seg.seat_costs?.[prevCls] : null) ?? seg.fare);
                        const newFare = toFareNum(entry?.fare ?? seg.seat_costs?.[display]);

                        seg.seat_class_ui = display;
                        seg.seat_avail_ui = entry
                            ? { ...entry, seat_class: display }
                            : { seat_class: display, avl: null, is_avail: null, fare: seg.seat_costs?.[display] ?? null };
                        if (entry && toFareNum(entry.fare) !== null) {
                            seg.seat_costs = { ...(seg.seat_costs || {}), [display]: entry.fare };
                        }

                        const total = toFareNum(route.total_fare);
                        if (total !== null && oldFare !== null && newFare !== null && newFare !== oldFare) {
                            route.total_fare = total + (newFare - oldFare);
                        }
                    });
                });
            };

            // The same route object sits in allRoutes and one category list. Immer copies
            // each path separately, so patch every list.
            patchRoutes(state.allRoutes);
            patchRoutes(state.trainRoutes);
            patchRoutes(state.flightRoutes);
            patchRoutes(state.busRoutes);
            patchRoutes(state.mixedRoutes);
        }
    }
});

export const { setLoading, setHeaderLoading, setError, clearRoutes, addRoutes, applySegmentAvailability } = travelSlice.actions;

// Selectors
export const selectAllRoutes = (state) => state.travel.allRoutes;
export const selectTrainRoutes = (state) => state.travel.trainRoutes;
export const selectFlightRoutes = (state) => state.travel.flightRoutes;
export const selectMixedRoutes = (state) => state.travel.mixedRoutes;
export const selectBusRoutes = (state) => state.travel.busRoutes;
export const selectTravelLoading = (state) => state.travel.loading;
export const selectHeaderLoading = (state) => state.travel.headerLoading;
export const selectTravelError = (state) => state.travel.error;
export const selectAvailAutoVersion = (state) => state.travel.availAutoVersion;

export default travelSlice.reducer;
